const SHEET_ID = import.meta.env.VITE_SPREADSHEET_ID;
const TOKEN_URL = import.meta.env.VITE_GRETA_TOKEN_URL;
const CHAT_ID = import.meta.env.VITE_CHAT_ID;

let cachedToken = { token: null, exp: 0 };
let sheetMeta = null;

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export async function getAccessToken() {
  if (cachedToken.token && Date.now() < cachedToken.exp) {
    return cachedToken.token;
  }

  const response = await fetch(TOKEN_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ chatId: CHAT_ID })
  });

  const json = await response.json();

  if (!response.ok || !json.success || !json.access_token) {
    throw new Error(json.error || 'ไม่สามารถขอสิทธิ์เข้าถึง Google Sheets ได้');
  }

  const expiresIn = Number(json.expires_in) || 3600;
  cachedToken = {
    token: json.access_token,
    exp: Date.now() + Math.max(5, expiresIn - 60) * 1000
  };

  return cachedToken.token;
}

export async function sheetsRequest(path, init = {}) {
  const token = await getAccessToken();
  const response = await fetch(
    `https://sheets.googleapis.com/v4/spreadsheets/${SHEET_ID}${path}`,
    {
      ...init,
      headers: {
        ...(init.headers || {}),
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }
  );

  if (response.status === 429) {
    await delay(700);
    return sheetsRequest(path, init);
  }

  if (!response.ok) {
    throw new Error(`Sheets API ${response.status}: ${await response.text()}`);
  }

  return response.json();
}

export async function getRows(range) {
  const data = await sheetsRequest(`/values/${encodeURIComponent(range)}`);
  return data.values || [];
}

export function appendRow(range, values) {
  return sheetsRequest(
    `/values/${encodeURIComponent(range)}:append?valueInputOption=USER_ENTERED&insertDataOption=INSERT_ROWS`,
    {
      method: 'POST',
      body: JSON.stringify({ values: [values] })
    }
  );
}

export function updateRow(range, values) {
  return sheetsRequest(
    `/values/${encodeURIComponent(range)}?valueInputOption=USER_ENTERED`,
    {
      method: 'PUT',
      body: JSON.stringify({ values: [values] })
    }
  );
}

export function clearSheetsCache() {
  sheetMeta = null;
}

const columnLetter = (number) => {
  let result = '';
  let current = number;

  while (current > 0) {
    const remainder = (current - 1) % 26;
    result = String.fromCharCode(65 + remainder) + result;
    current = Math.floor((current - 1) / 26);
  }

  return result;
};

export async function getSheetMeta(force = false) {
  if (!force && sheetMeta) {
    return sheetMeta;
  }

  const data = await sheetsRequest('/?fields=sheets.properties');
  sheetMeta = Object.fromEntries(
    (data.sheets || []).map((sheet) => [
      sheet.properties.title,
      sheet.properties.sheetId
    ])
  );

  return sheetMeta;
}

export async function getSheetIdByTitle(title) {
  let metadata = await getSheetMeta();

  if (metadata[title] !== undefined) {
    return metadata[title];
  }

  metadata = await getSheetMeta(true);
  return metadata[title];
}

export async function ensureTab(title, headers) {
  const metadata = await getSheetMeta();

  if (metadata[title] === undefined) {
    await sheetsRequest('/:batchUpdate', {
      method: 'POST',
      body: JSON.stringify({
        requests: [
          {
            addSheet: {
              properties: { title }
            }
          }
        ]
      })
    });

    clearSheetsCache();
  }

  const lastColumn = columnLetter(headers.length);
  const existingHeader = await getRows(`${title}!A1:${lastColumn}1`);

  if (!existingHeader.length || !existingHeader[0] || !existingHeader[0].length) {
    await updateRow(`${title}!A1:${lastColumn}1`, headers);
  }
}

export async function findRowIndexById(title, id) {
  const rows = await getRows(`${title}!A:A`);

  for (let index = 1; index < rows.length; index += 1) {
    if (rows[index] && rows[index][0] === id) {
      return index + 1;
    }
  }

  return -1;
}

export async function deleteRow(title, id) {
  const sheetId = await getSheetIdByTitle(title);

  if (sheetId === undefined) {
    throw new Error(`ไม่พบแท็บ ${title}`);
  }

  const sheetRow = await findRowIndexById(title, id);

  if (sheetRow < 0) {
    throw new Error(`ไม่พบข้อมูล ${id} ใน ${title}`);
  }

  const startIndex = sheetRow - 1;

  const result = await sheetsRequest('/:batchUpdate', {
    method: 'POST',
    body: JSON.stringify({
      requests: [
        {
          deleteDimension: {
            range: {
              sheetId,
              dimension: 'ROWS',
              startIndex,
              endIndex: startIndex + 1
            }
          }
        }
      ]
    })
  });

  clearSheetsCache();
  return result;
}